<script setup lang="ts">
import { Crisp } from 'crisp-sdk-web'
const runtime = useRuntimeConfig()
const { $um, $posthog } = useNuxtApp()
const { impersonate } = useSession()
const { isAuthenticated, user, roles, impersonating, loading } =
  storeToRefs(useSession())

defineProps<{
  rail: boolean
}>()

// Festival roles are issued per year ("2026 Selection Committee"); show this
// year's without the prefix, plus the standing ones.
const openFeature = useOpenFeature()
const visibleRoles = computed(() => {
  const year = `20${openFeature.getNumber('current_film_festival')} `
  return roles.value
    .filter(role => role.startsWith(year) || !/^\d{4} /.test(role))
    .map(role => role.replace(year, ''))
})

const login = () => {
  $um.signinRedirect()
}

const logout = async () => {
  try {
    await fetch(`https://${runtime.public.clerk.domain}/v1/client`, {
      method: 'delete',
      credentials: 'include',
    })
  } catch {
    console.warn('Unable to logout of Clerk')
  }

  Crisp.session.reset(false)
  $posthog().reset()
  await $um.signoutRedirect({
    post_logout_redirect_uri: window.location.origin,
  })
}
</script>

<template>
  <v-list nav density="compact" :class="{ rail }">
    <v-skeleton-loader
      v-if="loading"
      class="bg-transparent"
      type="list-item-avatar-two-line"
    />
    <v-tooltip v-else-if="!isAuthenticated" :disabled="!rail" text="Log in" location="end">
      <template #activator="{ props: tooltipProps }">
        <v-list-item
          v-bind="tooltipProps"
          title="Log in"
          subtitle="Register a team, submit films"
          prepend-icon="mdi-login"
          base-color="primary"
          @click="login"
        />
      </template>
    </v-tooltip>
    <v-menu v-else :location="rail ? 'end bottom' : 'top'" :offset="8">
      <template #activator="{ props: menuProps }">
        <v-list-item
          v-bind="menuProps"
          class="user"
          :title="user?.name || 'Account'"
          :subtitle="impersonating ? 'Masquerading' : user?.email"
        >
          <template #prepend>
            <v-badge
              :model-value="!!impersonating"
              color="error"
              icon="mdi-incognito"
              location="bottom end"
              bordered
            >
              <v-avatar size="32" color="surface-variant">
                <v-img v-if="user?.picture" :src="user.picture" :alt="user.name || ''" />
                <v-icon v-else icon="mdi-account" />
              </v-avatar>
            </v-badge>
          </template>
          <template #append>
            <v-icon icon="mdi-unfold-more-horizontal" size="small" />
          </template>
        </v-list-item>
      </template>

      <v-card min-width="280" max-width="320">
        <v-card-item class="pt-4">
          <template #prepend>
            <v-avatar size="44" color="surface-variant">
              <v-img v-if="user?.picture" :src="user.picture" :alt="user.name || ''" />
              <v-icon v-else icon="mdi-account" />
            </v-avatar>
          </template>
          <v-card-title class="text-body-1 font-weight-medium">{{ user?.name }}</v-card-title>
          <v-card-subtitle>{{ user?.email }}</v-card-subtitle>
        </v-card-item>

        <div v-if="visibleRoles.length" class="d-flex flex-wrap ga-1 px-4 pb-3">
          <v-chip
            v-for="role in visibleRoles"
            :key="role"
            size="x-small"
            label
            variant="tonal"
            color="primary"
          >
            {{ role }}
          </v-chip>
        </div>

        <v-alert
          v-if="impersonating"
          class="mx-3 mb-3"
          color="error"
          variant="tonal"
          density="compact"
          icon="mdi-incognito"
        >
          You're seeing the app as this user.
        </v-alert>

        <v-divider />

        <v-list density="compact" nav slim>
          <v-list-item
            v-if="impersonating"
            title="Stop masquerading"
            prepend-icon="mdi-incognito-off"
            base-color="error"
            @click="impersonate()"
          />
          <v-list-item
            title="Get help"
            href="https://help.sato48.com"
            target="_blank"
            prepend-icon="mdi-lifebuoy"
            append-icon="mdi-open-in-new"
          />
          <v-list-item
            title="Log out"
            :subtitle="impersonating ? 'Stop masquerading first' : undefined"
            prepend-icon="mdi-logout"
            :disabled="!!impersonating"
            @click="logout"
          />
        </v-list>
      </v-card>
    </v-menu>
  </v-list>
</template>

<style scoped>
/* Center the avatar in the 56px rail like the nav icons above it. */
.rail .user {
  padding-inline-start: 4px !important;
}
</style>
